import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Login.css';
import Navbar from '../Navigation/Navbar';
import {
    registerUser,
    getAllInstitutions,
    createUserInstitution,
    checkEmailExists,
    getAllUserInstitutions,
    getUserSpecializations,
    createUserSpecialization,
    isAuthenticated,
} from '../../api/users_institutions';
import { sendVerificationEmail, verifyEmailCode } from '../../api/mail_verification';
import { R2_MEDIA_BASE_URL } from '../../config/mediaConfig';

const USERNAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9._-]{2,29}$/;

const getPasswordConstraintError = (password, t) => {
    if (password.length < 8) {
        return t('common.passwordMinLength');
    }
    // if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
    //     return 'Password must include both uppercase and lowercase letters.';
    // }
    // if (!/\d/.test(password)) {
    //     return 'Password must include at least one number.';
    // }
    // if (!/[^A-Za-z0-9]/.test(password)) {
    //     return 'Password must include at least one special character.';
    // }
    return '';
};

function Signup() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [signupUsername, setSignupUsername] = useState('');
    const [signupName, setSignupName] = useState('');
    const [signupFamilyName, setSignupFamilyName] = useState('');
    const [signupEmails, setSignupEmails] = useState(['']);
    const [signupPassword, setSignupPassword] = useState('');
    const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [signupError, setSignupError] = useState('');

    const [institutions, setInstitutions] = useState([]);
    const validInstitutionDomains = useMemo(
        () => institutions.map((inst) => String(inst.domain || '').toLowerCase()),
        [institutions]
    );

    const [verificationEmail, setVerificationEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [verificationState, setVerificationState] = useState('input');
    const [verificationModalVisible, setVerificationModalVisible] = useState(false);
    const verificationResolveRef = useRef(null);
    const verificationTimerRef = useRef(null);

    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/dashboard');
        }
    }, [])

    useEffect(() => {
        loadInstitutions();
    }, []);

    const loadInstitutions = async () => {
        try {
            const institutions = await getAllInstitutions();
            setInstitutions(Array.isArray(institutions) ? institutions : []);
        } catch (err) {
            console.error('Failed to fetch institutions:', err);
        }
    };

    const resetVerificationModal = useCallback(() => {
        if (verificationTimerRef.current) {
            clearTimeout(verificationTimerRef.current);
            verificationTimerRef.current = null;
        }

        setVerificationCode(['', '', '', '', '', '']);
        setVerificationState('input');
        setVerificationEmail('');
        setVerificationModalVisible(false);
    }, []);

    const resolveVerification = useCallback((result) => {
        if (verificationResolveRef.current) {
            verificationResolveRef.current(result);
            verificationResolveRef.current = null;
        }
    }, []);

    const beginSignupEmailVerification = (email) => {
        setVerificationEmail(email);
        setVerificationCode(['', '', '', '', '', '']);
        setVerificationState('input');
        setVerificationModalVisible(true);

        sendVerificationEmail(email, i18n.language).catch((err) => {
            console.error('Failed to send verification email:', err);
        });

        return new Promise((resolve) => {
            verificationResolveRef.current = resolve;
        });
    };

    const closeVerificationModal = useCallback(() => {
        resolveVerification(false);
        resetVerificationModal();
    }, [resolveVerification, resetVerificationModal]);

    const updateVerificationCode = (index, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        setVerificationCode((prev) => prev.map((currentValue, currentIndex) => (currentIndex === index ? digit : currentValue)));

        if (digit && index < 5) {
            const nextField = document.getElementById(`signup-code-${index + 1}`);
            if (nextField) {
                nextField.focus();
            }
        }
    };

    const moveToNext = (current, nextFieldID) => {
        if (current && current.value && current.value.length >= 1) {
            if (nextFieldID) {
                const nextField = document.getElementById(nextFieldID);
                if (nextField) {
                    nextField.focus();
                }
            }
        }
    };

    const startVerificationProcess = async () => {
        if (verificationState === 'loading') {
            return;
        }

        const enteredCode = verificationCode.join('');
        setVerificationState('loading');

        let verified = false;
        try {
            const result = await verifyEmailCode(verificationEmail, enteredCode);
            verified = !!result?.verified;
        } catch (err) {
            console.error('Failed to verify code:', err);
        }

        if (verified) {
            setVerificationState('success');

            verificationTimerRef.current = window.setTimeout(() => {
                verificationTimerRef.current = null;
                resolveVerification(true);
                resetVerificationModal();
            }, 1200);
            return;
        }

        setVerificationState('error');

        verificationTimerRef.current = window.setTimeout(() => {
            verificationTimerRef.current = null;
            setVerificationState('input');
            setVerificationCode(['', '', '', '', '', '']);
        }, 1600);
    };

    const executeSuccessfulLink = async (registeredUser, email) => {
        const emailDomain = email.split('@')[1]?.toLowerCase();
        const institution = institutions.find((inst) => String(inst.domain || '').toLowerCase() === emailDomain);

        if (!registeredUser?.id || !institution) {
            return;
        }

        await createUserInstitution({
            user: registeredUser.id,
            email: String(email).trim().toLowerCase(),
            institution: institution.id,
            verified: true,
        });
    };

    const getInstitutionForEmail = useCallback((email) => {
        const emailDomain = String(email || '').split('@')[1]?.toLowerCase();
        if (!emailDomain) {
            return null;
        }

        return institutions.find((inst) => String(inst.domain || '').toLowerCase() === emailDomain) || null;
    }, [institutions]);

    const getRequiredSpecializationDomains = useCallback((emails) => {
        const domainsByKey = new Map();

        emails.forEach((email) => {
            const institution = getInstitutionForEmail(email);
            const targetDomain = String(institution?.type || '').trim();

            if (targetDomain) {
                domainsByKey.set(targetDomain.toLowerCase(), targetDomain);
            }
        });

        return Array.from(domainsByKey.values());
    }, [getInstitutionForEmail]);

    const syncUserSpecializationsForEmails = useCallback(async (user, emails) => {
        if (!user?.id || !Array.isArray(emails) || emails.length === 0) {
            return;
        }

        const requiredTargetDomains = getRequiredSpecializationDomains(emails);
        if (requiredTargetDomains.length === 0) {
            return;
        }

        try {
            const userSpecializations = await getUserSpecializations(user.id);
            const existingTargetDomains = new Set(
                (Array.isArray(userSpecializations) ? userSpecializations : [])
                    .map((specialization) => String(specialization.target_domain || '').trim().toLowerCase())
                    .filter(Boolean)
            );

            for (const targetDomain of requiredTargetDomains) {
                if (!existingTargetDomains.has(targetDomain.toLowerCase())) {
                    await createUserSpecialization(user.id, {
                        user: user.id,
                        target_domain: targetDomain,
                    });
                    existingTargetDomains.add(targetDomain.toLowerCase());
                }
            }
        } catch (err) {
            console.error('Failed to sync user specializations:', err);
        }
    }, [getRequiredSpecializationDomains]);

    const verifySignupEmail = async (email) => {
        setVerificationModalVisible(true);
        return beginSignupEmailVerification(email);
    };

    const closeSignup = useCallback(() => {
        setSignupError('');
        closeVerificationModal();
        navigate('/');
    }, [closeVerificationModal, navigate]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setSignupError('');
                closeVerificationModal();
                navigate('/');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [closeVerificationModal, navigate]);

    const handleRegister = async () => {
        setSignupError('');
        setLoading(true);

        try {
            const normalizedEmails = signupEmails.map((email) => email.trim().toLowerCase()).filter(Boolean);
            const duplicateInputs = normalizedEmails.filter((email, index) => normalizedEmails.indexOf(email) !== index);
            const trimmedEmails = Array.from(new Set(normalizedEmails));

            if (!signupName.trim()) {
                setSignupError(t('signup.errors.enterName'));
                return;
            }

            if (!signupFamilyName.trim()) {
                setSignupError(t('signup.errors.enterFamilyName'));
                return;
            }

            if (!USERNAME_PATTERN.test(signupUsername.trim())) {
                setSignupError(t('signup.errors.usernamePattern'));
                return;
            }

            if (trimmedEmails.length === 0) {
                setSignupError(t('signup.errors.provideEmail'));
                return;
            }

            if (duplicateInputs.length > 0) {
                setSignupError(t('signup.errors.uniqueEmail'));
                return;
            }

            for (const email of trimmedEmails) {
                const emailCheck = await checkEmailExists(email);
                if (emailCheck?.exists) {
                    setSignupError(t('signup.errors.emailAlreadyLinked', { email }));
                    return;
                }
            }

            for (const email of trimmedEmails) {
                const match = email.match(/@(.+)$/);
                if (!match) {
                    setSignupError(t('signup.errors.validEmailStructure'));
                    return;
                }

                const domain = match[1].toLowerCase();
                if (!validInstitutionDomains.includes(domain)) {
                    setSignupError(t('signup.errors.domainNotFound', { domain }));
                    return;
                }
            }

            if (!signupPassword.trim()) {
                setSignupError(t('signup.errors.createPassword'));
                return;
            }

            const passwordError = getPasswordConstraintError(signupPassword, t);
            if (passwordError) {
                setSignupError(passwordError);
                return;
            }

            if (signupPassword !== signupConfirmPassword) {
                setSignupError(t('signup.errors.passwordMismatch'));
                return;
            }

            const verifiedEmails = [];

            for (const email of trimmedEmails) {
                const isVerified = await verifySignupEmail(email);
                if (isVerified) {
                    verifiedEmails.push(email);
                }
            }

            if (verifiedEmails.length === 0) {
                setSignupError(t('signup.errors.noEmailsVerified'));
                return;
            }

            const registrationResponse = await registerUser({
                username: signupUsername.trim(),
                email: verifiedEmails[0],
                password: signupPassword,
                first_name: signupName.trim(),
                last_name: signupFamilyName.trim(),
            });

            if (registrationResponse?.token) {
                localStorage.setItem('authToken', registrationResponse.token);
            }

            if (registrationResponse?.user) {
                localStorage.setItem('user', JSON.stringify(registrationResponse.user));
            }

            const registeredUser = registrationResponse?.user || null;

            for (const email of verifiedEmails) {
                await executeSuccessfulLink(registeredUser, email);
            }

            if (registeredUser?.id) {
                await syncUserSpecializationsForEmails(registeredUser, verifiedEmails);
                await getAllUserInstitutions(registeredUser.id).catch(() => {});
            }

            localStorage.removeItem('authToken');
            localStorage.removeItem('user');

            setSignupName('');
            setSignupFamilyName('');
            setSignupUsername('');
            setSignupEmails(['']);
            setSignupPassword('');
            setSignupConfirmPassword('');
            setSignupError('');

            navigate('/login', { state: { prefillUsername: verifiedEmails[0] } });
        } catch (err) {
            console.error('Registration failed:', err);
            const message = typeof err === 'string'
                ? err
                : err?.username?.[0] || err?.email?.[0] || err?.password?.[0] || err?.detail || t('signup.errors.unableToCreateAccount');
            setSignupError(message);
        } finally {
            setLoading(false);
        }
    };

    const updateSignupEmail = (index, value) => {
        setSignupEmails(prev => prev.map((email, currentIndex) => (currentIndex === index ? value.toLowerCase() : email)));
    };

    const addSignupEmail = () => {
        setSignupEmails(prev => [...prev, '']);
    };

    const removeSignupEmail = (index) => {
        setSignupEmails(prev => prev.length === 1 ? [''] : prev.filter((_, currentIndex) => currentIndex !== index));
    };

    return (
        <>
            <Navbar />
            <div className="Login">
                <img src={`${R2_MEDIA_BASE_URL}/login/laugheffect.png`} alt={"Boy looking at login"} className="side-mascot-left" />

                <img src={`${R2_MEDIA_BASE_URL}/login/sneak.png`} alt={"Peeking girl looking up at login"} className="corner-mascot-right" />

                <div className="auth-bg-shapes">
                    <div className="shape shape-purple-1"></div>
                    <div className="shape shape-purple-2"></div>
                    <div className="shape shape-purple-3"></div>
                    <div className="bg-decorations">
                        <div className="dec-icon" style={{ top: '15%', left: '10%', backgroundImage: 'var(--svg-star)', width: '45px', height: '45px', animationDelay: '0s' }}></div>
                        <div className="dec-icon" style={{ top: '25%', right: '15%', backgroundImage: 'var(--svg-sparkle)', width: '50px', height: '50px', animationDelay: '1.5s' }}></div>

                        <div className="dec-icon" style={{ bottom: '20%', left: '25%', backgroundImage: 'var(--svg-glowing-bulb)', width: '60px', height: '60px', animationDelay: '3s', opacity: '0.3' }}></div>

                        <div className="dec-icon" style={{ top: '18%', right: '8%', backgroundImage: 'var(--svg-sparkle)', width: '45px', height: '45px', animationDelay: '2s', opacity: '0.25' }}></div>

                        <div className="dec-icon" style={{ bottom: '30%', right: '12%', backgroundImage: 'var(--svg-star)', width: '40px', height: '40px', animationDelay: '0.8s' }}></div>
                        <div className="dec-icon" style={{ top: '50%', left: '18%', backgroundImage: 'var(--svg-star)', width: '30px', height: '30px', animationDelay: '2.2s', opacity: '0.15' }}></div>

                        <div className="dec-icon" style={{ top: '10%', right: '35%', backgroundImage: 'var(--svg-glowing-bulb)', width: '40px', height: '40px', animationDelay: '1s', opacity: '0.25' }}></div>

                        <div className="dec-icon" style={{ bottom: '10%', left: '45%', backgroundImage: 'var(--svg-sparkle)', width: '35px', height: '35px', animationDelay: '2.5s', opacity: '0.2' }}></div>
                    </div>
                </div>

                <div className="login-card auth-card">
                    <button onClick={closeSignup} className="auth-close" aria-label={t('signup.closeDialog')}>&times;</button>
                    <div className="auth-card-top">
                        <div>
                            <h2>{t('signup.createAccount')}</h2>
                            <p>{t('signup.joinUs')}</p>
                        </div>
                    </div>

                    {signupError && <div className="auth-alert auth-alert-error">{signupError}</div>}

                    <input
                        placeholder={t('signup.firstNamePlaceholder')}
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        disabled={loading}
                    />
                    <input
                        placeholder={t('signup.familyNamePlaceholder')}
                        type="text"
                        value={signupFamilyName}
                        onChange={(e) => setSignupFamilyName(e.target.value)}
                        disabled={loading}
                    />

                    <input
                        placeholder={t('signup.usernamePlaceholder')}
                        type="text"
                        value={signupUsername}
                        onChange={(e) => setSignupUsername(e.target.value)}
                        disabled={loading}
                    />

                    <div className="email-fields" id="emailFieldsContainer">
                        {signupEmails.map((email, index) => (
                            <div className="email-row" key={index}>
                                <input
                                    placeholder={t('signup.institutionEmailPlaceholder')}
                                    type="email"
                                    className="inst-email-input"
                                    value={email}
                                    onChange={(e) => updateSignupEmail(index, e.target.value)}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="email-remove-btn"
                                    onClick={() => removeSignupEmail(index)}
                                    disabled={loading}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>

                    <button type="button" className="add-email-button" onClick={addSignupEmail} disabled={loading}>
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"></path></svg>
                        {t('signup.addAnotherEmail')}
                    </button>

                    <input
                        type="password"
                        placeholder={t('signup.createPasswordPlaceholder')}
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        disabled={loading}
                    />

                    <input
                        type="password"
                        placeholder={t('signup.confirmPasswordPlaceholder')}
                        value={signupConfirmPassword}
                        onChange={(e) => setSignupConfirmPassword(e.target.value)}
                        disabled={loading}
                    />

                    <button
                        className="btn-primary auth-submit"
                        onClick={handleRegister}
                        disabled={loading}
                    >
                        {loading ? t('signup.creatingAccount') : t('signup.createAccountButton')}
                    </button>

                    <div className="auth-switch-row">
                        <span>{t('signup.alreadyHaveAccount')}</span>
                        <button type="button" className="auth-link-button" onClick={() => navigate('/login')}>{t('common.logIn')}</button>
                    </div>
                </div>

                {verificationModalVisible && (
                    <div id="modalVerifyEmail" style={{ display: 'flex' }} className="modal-overlay-profile">
                        <div className="modal-box">
                            <span style={{ fontSize: '3.5rem' }}>📩</span>
                            <h2 style={{ margin: '15px 0 5px', justifySelf: 'center' }}>{t('common.verifyYourEmail')}</h2>
                            <p style={{ color: 'var(--text-muted)', lineHeight: 1.5, fontSize: '0.95rem' }}>
                                {t('common.enterCodeSentTo')} <br />
                                <strong id="verifyEmailTarget" style={{ color: 'var(--text-main)' }}>{verificationEmail || '...'}</strong><br />
                            </p>

                            {verificationState === 'input' && (
                                <div className="code-inputs">
                                    {verificationCode.map((value, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            className="code-input"
                                            id={`signup-code-${index}`}
                                            maxLength="1"
                                            value={value}
                                            onChange={(e) => updateVerificationCode(index, e.target.value)}
                                            onKeyUp={(e) => moveToNext(e.target, `signup-code-${index + 1}`)}
                                        />
                                    ))}
                                </div>
                            )}

                            {verificationState === 'loading' && (
                                <div id="loadingState">
                                    <div className="spinner"></div>
                                    <h3 style={{ marginTop: '25px', color: 'var(--text-main)' }}>{t('common.verifyingCode')}</h3>
                                </div>
                            )}

                            {verificationState === 'success' && (
                                <div id="successState">
                                    <div className="icon-pop">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon-svg success-icon" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6L9 17l-5-5"></path>
                                        </svg>
                                    </div>
                                    <h3 style={{ marginTop: '20px', color: '#10b981', fontWeight: 800 }}>{t('common.success')}</h3>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>{t('common.accountLinkedSuccessfully')}</p>
                                </div>
                            )}

                            {verificationState === 'error' && (
                                <div id="errorState">
                                    <div className="icon-shake">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon-svg error-icon" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </div>
                                    <h3 style={{ marginTop: '20px', color: '#ef4444', fontWeight: 800 }}>{t('common.invalidCode')}</h3>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>{t('common.checkCodeTryAgain')}</p>
                                </div>
                            )}

                            {verificationState === 'input' && (
                                <button className="btn-primary" style={{ width: '100%' }} onClick={startVerificationProcess}>{t('common.verifyAndLink')}</button>
                            )}
                            <button className="btn-primary" style={{ background: 'var(--bg-secondary)', color: 'var(--text-main)', border: '1px solid var(--border)', marginTop: '10px', width: '100%', boxShadow: 'none' }} onClick={closeVerificationModal}>{t('common.cancel')}</button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default Signup;
